import { interpolateRgb, interpolateRgbBasis } from 'd3-interpolate';
import { SPEED_UNIT, TEMPERATURE_UNIT } from './constants';
import { convertWindSpeed } from './unit-converters';

/**
 * Return a function which takes temperature values and returns the
 * colour for that value. Used in data visualisations
 *
 * @param isLight - use a light/pale colour scale instead of a more saturated colour scale
 * @param unit - see TEMPERATURE_UNIT (default C)
 * @returns {function(number): string}
 */
export function getTemperatureColourInterpolator(isLight, unit = TEMPERATURE_UNIT.C) {
	let colors;

	if (isLight) {
		colors = [
			'#EDA8FF',
			'#a8b8ff',
			'#99ffd2',
			'#cfffa8',
			'#fff3a8',
			'#ffcaa8',
			'#ffb0a8'
		];
	} else {
		colors = [
			'#CC00FF',
			'#002CDE',
			'#007A47',
			'#4CAD00',
			'#D1B800',
			'#FF6600',
			'#FF1600'
		];
	}

	const interpolator = interpolateRgbBasis(colors);

	return unit === TEMPERATURE_UNIT.F
		? temp => interpolator(Math.max(0, Math.min(1, (temp - 32) / 54)))
		: temp => interpolator(Math.max(0, Math.min(1, temp / 30)));
}

/**
 * Return a function which takes rainfall values and returns the
 * colour for that value. Used in data visualisations
 *
 * @param maxValue - value where the colour depth maxes out
 * @returns {function(number): string}
 */
export function getRainColourInterpolator(maxValue) {
	const interpolator = interpolateRgb(
		'#4BA7EB',
		'#0023AD'
	);

	return rainfall => interpolator(Math.min(1, rainfall / maxValue));
}

/**
 * Return a function which takes wind speed values and returns the
 * colour for that value. Used in data visualisations
 *
 * @param unit - see SPEED_UNIT (defaults to KMH)
 * @returns {function(number): string}
 */
export function getWindColourInterpolator(unit = SPEED_UNIT.KMH) {
	const interpolatorLow = interpolateRgb(
		'#222222',
		'#727272'
	);

	const interpolatorHigh = interpolateRgb(
		'#BD5A00',
		'#C70000'
	);

	const threshold = convertWindSpeed(50, unit);
	const upperRange = convertWindSpeed(40, unit);

	return speed => {
		const v = speed < threshold
			? speed / threshold
			: Math.min((speed - threshold) / upperRange, 1);

		return speed < threshold
			? interpolatorLow(v)
			: interpolatorHigh(v);
	};
}



// WEBPACK FOOTER //
// ./utils/interpolators.js